import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox } from './Checkbox';
import { cn } from '@/lib/utils';
import { CheckSquare, Search, Square, X } from 'lucide-react';


/**
 * Генерирует уникальный ID для чекбокса на основе метки и значения
 */
const generateCheckboxId = (label: string, item: string): string => {
    const normalizedLabel = label.toLowerCase().replace(/\s/g, '-');
    const normalizedItem = item.toLowerCase().replace(/\s/g, '-');
    return `${normalizedLabel}-${normalizedItem}`;
};

const useDebounce = <T,>(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

interface ListFilterProps {
    label: string;
    items:string[];
    selectedItems: string[];
    onChange: (item: string) => void;
    renderLabel?: (item: string) => string;
    getCounts?: (item: string) => number;
    scrollable?: boolean;
    hideLabel?: boolean;
    onClear?: () => void;
}

export function ListFilter({
   label,
   items,
   selectedItems,
   onChange,
   renderLabel,
   getCounts,
   scrollable = false,
   hideLabel = false,
   onClear
}: ListFilterProps) {
    const [searchQuery, setSearchQuery] = useState('');

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const showSearch = useMemo(() => items.length > 8, [items.length]);

    const filteredItems = useMemo(() => {
        if (!debouncedSearchQuery) return items;

        const lowerQuery = debouncedSearchQuery.toLowerCase();
        return items.filter(item => {
            const itemLabel = renderLabel ? renderLabel(item) : item;
            return itemLabel?.toLowerCase().includes(lowerQuery);
        });
    }, [items, debouncedSearchQuery, renderLabel]);

    const filteredSelectedCount = filteredItems.filter(item => selectedItems.includes(item)).length;
    const isAllFilteredSelected = filteredItems.length > 0 && filteredSelectedCount === filteredItems.length;

    const handleToggleAll = () => {
        if (isAllFilteredSelected) {
            // Deselect all filtered items
            filteredItems.forEach(item => {
                if (selectedItems.includes(item)) {
                    onChange(item);
                }
            });
        } else {
            // Select all filtered items
            filteredItems.forEach(item => {
                if (!selectedItems.includes(item)) {
                    onChange(item);
                }
            });
        }
    };

    const containerClasses = useMemo(() =>
            cn("flex flex-col", !hideLabel && "p-4 space-y-3"),
        [hideLabel]
    );

    const listClasses = useMemo(() =>
            cn(
                "space-y-0.5 px-4",
                (showSearch || !hideLabel) ? "py-2" : "py-4",
                scrollable && "max-h-[300px] overflow-y-auto pr-2 custom-scrollbar"
            ),
        [hideLabel, scrollable, showSearch]
    );

    const renderedItems = useMemo(() => {
        if (filteredItems.length === 0) {
            return <p className="text-xs text-zinc-500 py-2 text-center">Ничего не найдено</p>;
        }

        return filteredItems.map((item: string) => {
            const itemLabel = renderLabel ? renderLabel(item) : item;
            const checkboxId = generateCheckboxId(label, item);
            const count = getCounts ? getCounts(item) : undefined;

            return (
                <Checkbox
                    key={item}
                    id={checkboxId}
                    label={itemLabel}
                    checked={selectedItems.includes(item)}
                    onChange={() => onChange(item)}
                    count={count}
                />

            );
        });
    }, [filteredItems, selectedItems, label, renderLabel, getCounts, onChange]);



    return (
        <div className={containerClasses}>
            {!hideLabel && (
                <div className="flex items-center justify-between px-4">
                    <h3 className="text-sm font-bold text-zinc-100 uppercase tracking-wider">{label}</h3>
                </div>
            )}

            {showSearch && (
                <div className="px-4 py-3 border-b border-zinc-900/50 bg-zinc-900/10">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <Search className="w-3.5 h-3.5 text-zinc-500" />
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={`Поиск...`}
                            className="w-full rounded bg-zinc-950 border border-zinc-800 py-2 pl-8 pr-8 text-xs text-white focus:border-brand-gold focus:outline-none transition-colors placeholder-zinc-600"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-zinc-500 hover:text-white"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Bulk actions area - only shown if search is actively being performed */}
            {searchQuery.trim().length > 0 && filteredItems.length > 1 && (
                <div className="px-4 py-2 border-b border-zinc-900 flex items-center justify-between bg-zinc-900/20">
                    <button
                        type="button"
                        onClick={handleToggleAll}
                        className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-brand-gold transition-colors flex items-center gap-1.5"
                    >
                        {isAllFilteredSelected ? (
                            <>
                                <Square className="w-3 h-3" />
                                Снять все
                            </>
                        ) : (
                            <>
                                <CheckSquare className="w-3 h-3" />
                                Выбрать все
                            </>
                        )}
                    </button>
                    <span className="text-[10px] text-zinc-600 font-bold uppercase tracking-tighter">
                        {filteredSelectedCount} / {filteredItems.length}
                    </span>
                </div>
            )}

            <div className={listClasses}>
                {renderedItems}
            </div>

            {onClear && selectedItems.length > 0 && (
                <div className="px-4 py-2 border-t border-zinc-800/30">
                    <button
                        onClick={onClear}
                        className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-red-400 transition-colors flex items-center gap-1.5 ml-auto"
                    >
                        <X className="w-3 h-3" />
                        Очистить
                    </button>
                </div>
            )}
        </div>
    );
}
